import {Modal, Flex, Button, Group, Text} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import {getChemicalFileContent, guessChemicalFileFormat} from 'chemistry/ChemicalFileUtil';
import {EXPORT_DISPLAY_NAME, REACTION_FORMATS} from 'chemistry/ChemistryConstants';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {
  CHEMICAL_FILE_FORMAT_TO_MARVIN_FORMAT,
  isMarvinCompatibleFileFormat,
  MarvinChemicalFormat,
} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import notifications from 'notifications';
import ModalFooter from 'ui-library/modals/ModalFooter';

export interface StructureWithFormat {
  structure: ChemicalStructureDrawerValue;
  format: MarvinChemicalFormat;
}

const REACTION_SCHEME_SUPPORTED_FORMATS = REACTION_FORMATS.map((format) => EXPORT_DISPLAY_NAME[format]).join(
  ', '
);

export const UNSUPPORTED_REACTION_SCHEME_FILE_MESSAGE = `This file type isn't supported for reaction schemes. Supported formats: ${REACTION_SCHEME_SUPPORTED_FORMATS}.`;

export async function getStructureWithFormatFromFile(file: File): Promise<StructureWithFormat | null> {
  const guessResult = guessChemicalFileFormat(file);
  if (guessResult.kind === 'unrecognized' || !isMarvinCompatibleFileFormat(guessResult.format)) {
    notifications.error(UNSUPPORTED_REACTION_SCHEME_FILE_MESSAGE, {
      id: 'reactionSchemeUnsupportedFile',
    });
    return null;
  }
  const fileContent = await getChemicalFileContent(guessResult.format, file);
  return {
    structure: fileContent,
    format: CHEMICAL_FILE_FORMAT_TO_MARVIN_FORMAT[guessResult.format],
  };
}

interface ReactionSketcherModalProps {
  onClose: () => void;
  onSubmit: (newMarvinStructure: string) => Promise<void>;
  onChangeChemicalStructure?: () => void;
  chemicalStructureError?: string | null;
  initialStructureData?: StructureWithFormat;
}

export default function ReactionSketcherModal({
  onClose,
  onSubmit,
  chemicalStructureError,
  onChangeChemicalStructure,
  initialStructureData,
}: ReactionSketcherModalProps): JSX.Element {
  const [marvinStructure, setMarvinStructure] = useState<ChemicalStructureDrawerValue>(null);
  const [hasReactionContents, setHasReactionContents] = useState(false);

  const handleMoleculeChange = useCallback(
    (value: ChemicalStructureDrawerValue) => {
      setMarvinStructure(value);
      onChangeChemicalStructure?.();
    },
    [onChangeChemicalStructure]
  );

  return (
    <Modal
      title="Reaction Sketcher"
      opened={true}
      onClose={onClose}
      size="xl"
      styles={{
        content: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          flex: 1,
        },
      }}
    >
      <Flex direction="column" h="100%">
        <ChemicalStructureDrawer
          mode={ChemicalStructureDrawerMode.REACTION}
          onMoleculeChange={handleMoleculeChange}
          setHasReactionContents={setHasReactionContents}
          initialValue={initialStructureData?.structure ?? null}
          inputFormat={initialStructureData?.format}
          outputFormat={MarvinChemicalFormat.MRV}
        />
        <ModalFooter>
          <Group position="right">
            {chemicalStructureError && (
              <Text size="xs" color="red">
                {chemicalStructureError}
              </Text>
            )}
            <Button type="button" onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!hasReactionContents || marvinStructure == null || Boolean(chemicalStructureError)}
              onClick={() => {
                onSubmit(marvinStructure!);
              }}
            >
              Save
            </Button>
          </Group>
        </ModalFooter>
      </Flex>
    </Modal>
  );
}
